import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api, { type CustomerPortalResponse } from '../lib/api'
import LifecycleBoard from '../components/LifecycleBoard'
import UnifiedTimeline from '../components/UnifiedTimeline'
import { SupportReadOnlyNotice, useSupportReadOnlyView } from '../lib/support-session'

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : 'Not specified'
}

function formatShortDate(value?: string | null) {
  return value ? new Date(value).toLocaleDateString() : 'Not specified'
}

function formatMoney(cents?: number | null, currency = 'USD') {
  if (typeof cents !== 'number') return 'Pending'
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
}

const progressSteps = [
  'Quote Sent',
  'Quote Accepted',
  'Order Draft',
  'Customer Info In Progress',
  'Customer Submitted',
  'Agreement Signed',
  'Payment Pending / Net Terms Review',
  'Dispatch Review',
  'Ready to Dispatch',
]

const navItems = [
  { id: 'get-a-quote', label: 'Get a Quote' },
  { id: 'my-quotes', label: 'My Quotes' },
  { id: 'my-orders', label: 'My Orders' },
  { id: 'track-vehicle', label: 'Track Vehicle' },
  { id: 'documents', label: 'Documents' },
]

export default function CustomerPortalPage() {
  const { token = '' } = useParams<{ token: string }>()
  const qc = useQueryClient()
  const { isSupportReadOnlyView } = useSupportReadOnlyView()
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<Record<string, any>>({})

  const portalQuery = useQuery({
    queryKey: ['customer-portal', token],
    queryFn: () => api.customerPortal.get(token).then((r) => r.data),
    enabled: !!token,
  })

  const customerTimelineQuery = useQuery({
    queryKey: ['customer-portal-timeline', token],
    queryFn: async () => (await api.customerPortal.timeline(token)).data.timeline,
    enabled: !!token,
  })

  useEffect(() => {
    const data = portalQuery.data
    if (!data?.order) return
    setForm({
      pickupContactName: data.order.pickupContactName ?? '',
      pickupContactPhone: data.order.pickupContactPhone ?? '',
      deliveryContactName: data.order.deliveryContactName ?? '',
      deliveryContactPhone: data.order.deliveryContactPhone ?? '',
      pickupAccessNotes: data.order.pickupAccessNotes ?? '',
      deliveryAccessNotes: data.order.deliveryAccessNotes ?? '',
      stockNumber: data.order.stockNumber ?? '',
      referenceNumber: data.order.referenceNumber ?? '',
      pickupWindowStart: data.order.pickupWindowStart ?? '',
      pickupWindowEnd: data.order.pickupWindowEnd ?? '',
      deliveryWindowStart: data.order.deliveryWindowStart ?? '',
      deliveryWindowEnd: data.order.deliveryWindowEnd ?? '',
      vehicleYear: data.order.customerVehicleDetails?.year ?? '',
      vehicleMake: data.order.customerVehicleDetails?.make ?? '',
      vehicleModel: data.order.customerVehicleDetails?.model ?? '',
      vin: data.order.customerVehicleDetails?.vin ?? '',
      operable: data.order.customerVehicleDetails?.operable ?? true,
    })
  }, [portalQuery.data])

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.customerPortal.updateOrder(token, payload),
    onSuccess: (response) => {
      qc.setQueryData(['customer-portal', token], response.data)
      setMessage(response.data.reviewRequired ? 'Dispatcher review required for the requested change.' : 'Order details saved.')
      setError(null)
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error ?? 'Unable to save customer portal details.')
      setMessage(null)
    },
  })

  const agreementMutation = useMutation({
    mutationFn: () => api.customerPortal.acknowledgeAgreement(token),
    onSuccess: (response) => {
      qc.setQueryData(['customer-portal', token], (current: any) => ({ ...current, order: response.data.order }))
      setMessage('Agreement acknowledged.')
      setError(null)
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error ?? 'Unable to acknowledge the agreement.')
      setMessage(null)
    },
  })

  const submitMutation = useMutation({
    mutationFn: () => api.customerPortal.submitOrder(token),
    onSuccess: (response) => {
      qc.setQueryData(['customer-portal', token], (current: any) => ({ ...current, order: response.data.order }))
      setMessage('Shipment details submitted.')
      setError(null)
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error ?? 'Unable to submit shipment details.')
      setMessage(null)
    },
  })

  const portal = portalQuery.data
  const order = portal?.order ?? null
  const quote = portal?.quoteResponse ?? null
  const documents = portal?.documents ?? []
  const scopedTitle = portal?.portal.accountCompanyName || 'Customer Portal'
  const statusLabel = order ? `${order.customerCompletionState ?? 'CUSTOMER_INFO_IN_PROGRESS'} · ${order.paymentState ?? 'PAYMENT_PENDING'}` : 'Awaiting order draft'
  const activeStep = useMemo(() => {
    if (!order) return 0
    if (order.customerChangeRequestState === 'REVIEW_REQUIRED') return 6
    if (order.customerCompletionState === 'AGREEMENT_SIGNED') return 5
    if (order.customerSubmissionState === 'SUBMITTED') return 4
    return 3
  }, [order])

  const lifecycleCard = useMemo(() => {
    if (!portal?.quoteResponse) return null

    const missing: string[] = []
    const blocked: string[] = []

    if (order && !order.deliveryContactName) missing.push('Missing delivery contact.')
    if (order && !order.deliveryContactPhone) missing.push('Missing delivery contact phone.')
    if (order && (!order.deliveryWindowStart || !order.deliveryWindowEnd)) missing.push('Missing delivery appointment window.')

    if (order?.customerChangeRequestState === 'REVIEW_REQUIRED') {
      blocked.push(order.customerChangeRequestReason || 'Dispatcher review required for requested changes.')
    }

    const quoteStatus = String(portal.portal.quoteStatus || '').toLowerCase()
    let currentStatus = 'Quote Accepted'
    let owner = 'Customer'
    let nextBestAction = 'Complete shipment details'
    let nextActionTo: string | undefined

    if (quoteStatus === 'quote_sent') {
      currentStatus = 'Quote Sent'
      owner = 'Customer'
      nextBestAction = 'Accept quote'
    } else if (order?.customerSubmissionState === 'SUBMITTED') {
      currentStatus = 'Customer Submitted'
      owner = 'Dispatcher'
      nextBestAction = order?.customerChangeRequestState === 'REVIEW_REQUIRED' ? 'Dispatch review pending' : 'Dispatch review and mark ready'
      nextActionTo = '/dispatcher'
    } else if (order?.customerCompletionState === 'CUSTOMER_INFO_IN_PROGRESS') {
      currentStatus = 'Customer Completing Details'
      owner = 'Customer'
      nextBestAction = 'Submit shipment details'
    }

    const timeline = [
      { label: 'Quote Sent', at: portal.quoteResponse.quotedAt || portal.quoteResponse.createdAt },
      ...(portal.quoteResponse.customerAcceptedAt ? [{ label: 'Quote Accepted', at: portal.quoteResponse.customerAcceptedAt }] : []),
      ...(order ? [{ label: 'Customer Completing Details', at: order.updatedAt }] : []),
      ...(order?.customerSubmittedAt ? [{ label: 'Customer Submitted', at: order.customerSubmittedAt }] : []),
      ...(order?.customerChangeRequestState === 'REVIEW_REQUIRED' ? [{ label: 'Dispatch Review', at: order.updatedAt }] : []),
      ...(order?.status === 'READY' ? [{ label: 'Ready for Dispatch', at: order.updatedAt }] : []),
    ]

    return { currentStatus, owner, missing, blocked, nextBestAction, nextActionTo, timeline }
  }, [order, portal])

  if (portalQuery.isLoading) {
    return <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center text-sm text-slate-400">Loading customer portal…</div>
  }

  if (portalQuery.isError || !portal) {
    return <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center text-sm text-red-300">{(portalQuery.error as any)?.response?.data?.error || 'Customer portal not found.'}</div>
  }

  const save = () => {
    if (isSupportReadOnlyView) return
    if (!order) return
    updateMutation.mutate({
      pickupContactName: form.pickupContactName,
      pickupContactPhone: form.pickupContactPhone,
      deliveryContactName: form.deliveryContactName,
      deliveryContactPhone: form.deliveryContactPhone,
      pickupAccessNotes: form.pickupAccessNotes,
      deliveryAccessNotes: form.deliveryAccessNotes,
      stockNumber: form.stockNumber,
      referenceNumber: form.referenceNumber,
      pickupWindowStart: form.pickupWindowStart || null,
      pickupWindowEnd: form.pickupWindowEnd || null,
      deliveryWindowStart: form.deliveryWindowStart || null,
      deliveryWindowEnd: form.deliveryWindowEnd || null,
      vehicleYear: form.vehicleYear || null,
      vehicleMake: form.vehicleMake || null,
      vehicleModel: form.vehicleModel || null,
      vin: form.vin || null,
      operable: Boolean(form.operable),
    })
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(20,184,166,0.18),_transparent_32%),linear-gradient(180deg,#02141c_0%,#081b26_100%)] text-slate-100">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="rounded-3xl border border-white/10 bg-white/6 backdrop-blur-xl shadow-2xl shadow-black/20 overflow-hidden">
          <div className="flex flex-col gap-4 border-b border-white/10 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.35em] text-cyan-200/80">Customer Workspace</div>
              <h1 className="mt-2 text-3xl font-semibold text-white">{scopedTitle}</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-300">Use this portal to finish shipment details, review your order status, and keep documents in one scoped workspace.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-slate-300">
              <div className="font-semibold text-white">{portal.portal.quoteStatus}</div>
              <div className="mt-1">Payment: {portal.portal.paymentState}</div>
              <div className="text-xs text-slate-400">Open in browser safe for mobile.</div>
            </div>
          </div>

          <nav className="flex flex-wrap gap-2 px-6 py-4">
            {navItems.map((item) => (
              <a key={item.id} href={`#${item.id}`} className="rounded-full border border-white/10 bg-slate-950/50 px-4 py-2 text-sm text-slate-200 transition hover:bg-slate-900">
                {item.label}
              </a>
            ))}
          </nav>
        </header>

        {message && <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{message}</div>}
        {error && <div className="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{error}</div>}
        <SupportReadOnlyNotice className="mt-4" />

        {lifecycleCard && (
          <div className="mt-4">
            <LifecycleBoard
              title="Quote to Dispatch Lifecycle"
              currentStatus={lifecycleCard.currentStatus}
              owner={lifecycleCard.owner}
              missing={lifecycleCard.missing}
              blocked={lifecycleCard.blocked}
              nextBestAction={lifecycleCard.nextBestAction}
              nextActionTo={lifecycleCard.nextActionTo}
              timeline={lifecycleCard.timeline}
            />
          </div>
        )}

        <div className="mt-4 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <section id="get-a-quote" className="rounded-3xl border border-white/10 bg-white/6 p-5 backdrop-blur-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-cyan-200/70">Get a Quote</div>
                <h2 className="mt-1 text-xl font-semibold text-white">Quote intake stays fast</h2>
              </div>
              <Link to="/q/wahatoya-auto-group" className="rounded-xl border border-cyan-400/30 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-100 hover:bg-cyan-400/20">
                Open QuotePort
              </Link>
            </div>
            <p className="mt-3 text-sm text-slate-300">If you need another price request, use QuotePort. It keeps the quote step lightweight and separate from order completion.</p>
          </section>

          <section id="my-quotes" className="rounded-3xl border border-white/10 bg-white/6 p-5 backdrop-blur-xl">
            <div className="text-xs uppercase tracking-[0.3em] text-cyan-200/70">My Quotes</div>
            {quote ? (
              <div className="mt-3 space-y-2 text-sm text-slate-200">
                <div className="font-semibold text-white">{quote.customerName}</div>
                <div>{quote.pickupCity}, {quote.pickupState} {quote.pickupZip} → {quote.deliveryCity}, {quote.deliveryState} {quote.deliveryZip}</div>
                <div>Quote status: <span className="capitalize">{quote.status}</span></div>
                <div>Quoted price: {formatMoney(quote.quotedCustomerPriceCents, quote.quotedCurrency || 'USD')}</div>
                <div className="text-xs text-slate-400">Response link token is scoped to this record only.</div>
              </div>
            ) : (
              <div className="mt-3 text-sm text-slate-300">No quote loaded.</div>
            )}
          </section>
        </div>

        <section id="my-orders" className="mt-4 rounded-3xl border border-white/10 bg-white/6 p-5 backdrop-blur-xl">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-cyan-200/70">My Orders</div>
              <h2 className="mt-1 text-xl font-semibold text-white">Order completion</h2>
              <p className="mt-1 text-sm text-slate-300">Fill in the missing contact and timing details, then submit the order for dispatch review.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-slate-300">{statusLabel}</div>
          </div>

          {!order ? (
            <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-300">This record has not been turned into an order draft yet.</div>
          ) : (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <fieldset disabled={isSupportReadOnlyView} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-white">Shipment Details</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.pickupContactName ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, pickupContactName: e.target.value }))} placeholder="Pickup contact" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.pickupContactPhone ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, pickupContactPhone: e.target.value }))} placeholder="Pickup phone" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.deliveryContactName ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, deliveryContactName: e.target.value }))} placeholder="Delivery contact" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.deliveryContactPhone ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, deliveryContactPhone: e.target.value }))} placeholder="Delivery phone" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.pickupAccessNotes ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, pickupAccessNotes: e.target.value }))} placeholder="Pickup gate / access notes" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.deliveryAccessNotes ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, deliveryAccessNotes: e.target.value }))} placeholder="Delivery gate / access notes" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.stockNumber ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, stockNumber: e.target.value }))} placeholder="Stock number" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.referenceNumber ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, referenceNumber: e.target.value }))} placeholder="Internal reference" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input type="datetime-local" className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white" value={form.pickupWindowStart ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, pickupWindowStart: e.target.value }))} />
                  <input type="datetime-local" className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white" value={form.pickupWindowEnd ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, pickupWindowEnd: e.target.value }))} />
                  <input type="datetime-local" className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white" value={form.deliveryWindowStart ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, deliveryWindowStart: e.target.value }))} />
                  <input type="datetime-local" className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white" value={form.deliveryWindowEnd ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, deliveryWindowEnd: e.target.value }))} />
                </div>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={save} disabled={updateMutation.isPending} className="rounded-xl bg-cyan-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 disabled:opacity-50">
                    {updateMutation.isPending ? 'Saving…' : 'Save Shipment Details'}
                  </button>
                  <button type="button" onClick={() => agreementMutation.mutate()} disabled={agreementMutation.isPending} className="rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-50">
                    {agreementMutation.isPending ? 'Working…' : 'Agreement Signed'}
                  </button>
                </div>
              </fieldset>

              <fieldset disabled={isSupportReadOnlyView} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-white">Vehicle Details</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.vehicleYear ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, vehicleYear: e.target.value }))} placeholder="Year" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.vehicleMake ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, vehicleMake: e.target.value }))} placeholder="Make" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500" value={form.vehicleModel ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, vehicleModel: e.target.value }))} placeholder="Model" />
                  <input className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500 uppercase" value={form.vin ?? ''} onChange={(e) => setForm((prev) => ({ ...prev, vin: e.target.value.toUpperCase() }))} placeholder="VIN" maxLength={20} />
                </div>
                <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-950/60 px-3 py-3 text-sm text-slate-200">
                  <input type="checkbox" checked={Boolean(form.operable)} onChange={(e) => setForm((prev) => ({ ...prev, operable: e.target.checked }))} className="h-4 w-4 rounded border-white/20 bg-slate-900" />
                  Operable
                </label>
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-50">
                  {order.customerChangeRequestState === 'REVIEW_REQUIRED'
                    ? `Change Requested / Dispatcher Review Required: ${order.customerChangeRequestReason ?? 'A commercial detail needs review.'}`
                    : 'Only missing details should be filled here. Changes to price, route, vehicle count, or core vehicle identity will trigger review.'}
                </div>
                <button type="button" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending} className="w-full rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-sm font-semibold text-white hover:bg-white/15 disabled:opacity-50">
                  {submitMutation.isPending ? 'Submitting…' : 'Submit For Dispatch Review'}
                </button>
              </fieldset>
            </div>
          )}
        </section>

        <section id="track-vehicle" className="mt-4 rounded-3xl border border-white/10 bg-white/6 p-5 backdrop-blur-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-200/70">Track Vehicle</div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {progressSteps.map((step, index) => (
              <div key={step} className={`rounded-2xl border px-4 py-3 text-sm ${index <= activeStep ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-50' : 'border-white/10 bg-slate-950/40 text-slate-300'}`}>
                <div className="font-semibold">{step}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-400">The portal only shows scoped order state, payment state, and vehicle status for this customer record.</div>
        </section>

        <div className="mt-4">
          <UnifiedTimeline title="Customer Timeline" entries={customerTimelineQuery.data ?? []} defaultFilter="customer" />
        </div>

        <section id="documents" className="mt-4 rounded-3xl border border-white/10 bg-white/6 p-5 backdrop-blur-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-200/70">Documents</div>
          <div className="mt-3 space-y-3">
            {!documents.length && <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-300">No documents or receipts have been published yet.</div>}
            {documents.map((doc) => (
              <div key={doc.id} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-200">
                <div className="font-semibold text-white">{doc.label}</div>
                <div className="text-xs text-slate-400">{doc.docType} · {doc.status} · {formatShortDate(doc.createdAt)}</div>
              </div>
            ))}
            {order && (
              <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-200">
                <div className="font-semibold text-white">Order #{order.orderNumber}</div>
                <div className="text-xs text-slate-400">Payment state: {order.paymentState ?? portal.portal.paymentState} · Agreement: {order.customerAgreementSignedAt ? 'Signed' : 'Pending'}</div>
                <div className="text-xs text-slate-400">Shipment value: {formatMoney(order.customerPriceCents, 'USD')}</div>
                <div className="text-xs text-slate-400">Last updated: {formatDate(order.updatedAt)}</div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
